import { Temporal } from 'temporal-polyfill';

/**
 * Returns the latest (maximum) instant out of an argument list of at least one `Temporal.Instant` object.
 *
 * @throws {TypeError} At least one Temporal.Instant is required to find the max value
 */
export function maxInstant(...instants: [Temporal.Instant, ...Temporal.Instant[]]): Temporal.Instant {
  if (instants.length === 0) {
    throw new Error('At least one Temporal.Instant is required to find the max value');
  }

  return instants.reduce((max, current) => (Temporal.Instant.compare(current, max) > 0 ? current : max));
}

/**
 * Returns the earliest (minimum) instant out of an argument list of at least one `Temporal.Instant` object.
 *
 * @throws {TypeError} At least one Temporal.Instant is required to find the min value
 */
export function minInstant(...instants: [Temporal.Instant, ...Temporal.Instant[]]): Temporal.Instant {
  if (instants.length === 0) {
    throw new Error('At least one Temporal.Instant is required to find the min value');
  }

  return instants.reduce((min, current) => (Temporal.Instant.compare(current, min) < 0 ? current : min));
}

/**
 * Returns the difference between two `Temporal.Instant` values in a specific time unit.
 *
 * Why this function exists:
 * It simplifies a common use case where you want the numeric difference between two instants
 * in a single unit (e.g., seconds, hours), without having to write `to.since(from)[unit]`
 * and repeat the unit twice.
 *
 * When not to use this function:
 * - When you need precise control over rounding, balancing, or duration formatting.
 * - When you need to inspect the full `Temporal.Duration` result (e.g., across multiple units).
 *
 * Note:
 * This uses `roundingMode: 'trunc'` to always round **towards zero**.
 * That means it returns the number of **whole units** that have passed, without rounding up.
 * This is usually what you want for comparisons like `< 12 hours` or `> 24 hours`.
 *
 * Avoid using this if you need more precise or configurable rounding semantics.
 *
 * @param from - The earlier `Temporal.Instant`
 * @param to - The later `Temporal.Instant`
 * @param unit - The time unit to return the difference in (`Temporal.TimeUnit`)
 * @returns The numeric difference in the given unit
 *
 * @example
 * ```ts
 * const earlier = Temporal.Instant.from('2025-06-01T12:00Z');
 * const later = Temporal.Instant.from('2025-06-01T14:30Z');
 * const hours = differenceInTimeUnit(earlier, later, 'hour'); // Returns 2
 * ```
 */
export function differenceInTimeUnit(from: Temporal.Instant, to: Temporal.Instant, unit: Temporal.TimeUnit): number {
  const diff = to.since(from, { largestUnit: unit, roundingMode: 'trunc' });
  return diff[timeUnitToDurationKey(unit)];
}

function timeUnitToDurationKey(unit: Temporal.TimeUnit): keyof Temporal.DurationLike {
  switch (unit) {
    case 'hour':
      return 'hours';
    case 'minute':
      return 'minutes';
    case 'second':
      return 'seconds';
    case 'millisecond':
      return 'milliseconds';
    case 'microsecond':
      return 'microseconds';
    case 'nanosecond':
      return 'nanoseconds';
    default:
      throw new Error(`Unknown time unit: ${unit}`);
  }
}
